import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Alert
} from '@mui/material';
import {
  Delete,
  Email,
  Schedule,
  CheckCircle,
  Warning,
  Security
} from '@mui/icons-material';

const DataDeletion = () => {
  const handleEmailClick = () => {
    window.location.href = 'mailto:bddashboardcrm@gmail.com?subject=Data Deletion Request';
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 4, mb: 3, textAlign: 'center', bgcolor: 'error.50' }}>
        <Delete sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom color="error.main">
          Data Deletion Instructions
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Your privacy and data control rights matter to us
        </Typography>
      </Paper>

      {/* Main Content */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="primary" />
            How to Request Data Deletion
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            If you want to delete your data collected by BD Dashboard, please send an email to{' '}
            <strong>bddashboardcrm@gmail.com</strong> with the subject{' '}
            <strong>"Data Deletion Request"</strong>. We will process your request within{' '}
            <strong>7 working days</strong>.
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={<Email />}
            onClick={handleEmailClick}
            sx={{ mt: 2, mb: 3 }}
          >
            Send Deletion Request Email
          </Button>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            What to Include in Your Request:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Email Subject"
                secondary="Use exactly: 'Data Deletion Request'"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Your Account Information"
                secondary="Include your registered email address and full name"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Verification Details"
                secondary="Any additional information to help us verify your identity"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Specific Data (Optional)"
                secondary="Specify particular data types if you want selective deletion"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Process Timeline */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule color="primary" />
            Deletion Process Timeline
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.8rem'
                }}>
                  1
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Request Received (Day 1)"
                secondary="We acknowledge receipt of your deletion request within 24 hours"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  bgcolor: 'info.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.8rem'
                }}>
                  2
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Identity Verification (Days 1-3)"
                secondary="We verify your identity and account ownership for security"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  bgcolor: 'warning.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.8rem'
                }}>
                  3
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Data Removal (Days 4-6)"
                secondary="We permanently delete your data from all systems and backups"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  bgcolor: 'success.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.8rem'
                }}>
                  4
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Confirmation (Day 7)"
                secondary="We send you confirmation that your data has been completely deleted"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Important Notes */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Important Notes:</strong>
        </Typography>
        <List dense>
          <ListItem sx={{ py: 0 }}>
            <ListItemText 
              primary="• Data deletion is permanent and cannot be undone"
            />
          </ListItem>
          <ListItem sx={{ py: 0 }}>
            <ListItemText 
              primary="• Some data may be retained for legal compliance purposes as required by medical regulations"
            />
          </ListItem>
          <ListItem sx={{ py: 0 }}>
            <ListItemText 
              primary="• Your account will be deactivated permanently after data deletion"
            />
          </ListItem>
          <ListItem sx={{ py: 0 }}>
            <ListItemText 
              primary="• Anonymous usage statistics may be retained for service improvement"
            />
          </ListItem>
        </List>
      </Alert>

      {/* Contact Information */}
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Need Help or Have Questions?
          </Typography>
          <Typography variant="body1" paragraph>
            If you need assistance with your data deletion request or have questions about our data practices, 
            please don't hesitate to contact us.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email: <strong>bddashboardcrm@gmail.com</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Response Time: Within 24 hours during business days
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DataDeletion;